import { ViewType, ViewObject } from '../base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { dataService } from '../../data';

import { Product } from '../objects/product';

import { PlaceView } from './place';
import { PreselectView } from './preselect';
import { CategoryView } from './category';
import { ProductOptView } from './productopt';
import { FamilyView } from './family';
import { QrCodeView } from './qrcode';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _ProductView extends ViewObject<Product> {
    
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get product(): Product{
        return this.object;
    }
             
    constructor(object: Product, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _groupitems: Array <ProductView> | null = null;
    private _validselects: Array <PreselectView> | null = null;
    private _validcategories: Array <CategoryView> | null = null;
    private _validoptions: Array <ProductOptView> | null = null;
    private _validtocart: boolean | null = null;
    private _calcpriceinfo: PriceType | null = null;
    private _lastproduct: ProductView | null = null;

    private _productslength = 0;
    private _catalogslength = 0;

    private RefreshView(targets: string[]){
        if (targets.includes('PRODUCT')){
            this._groupitems = null;
            this._calcpriceinfo = null;
            this._validtocart = null;
            this._lastproduct = null;
            this._validselects = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCTCHECKED')){
            this.DoRefreshView();
        }

        if (targets.includes('PRESELECT')){
            this._validselects = null;     
            this.DoRefreshView();
        }

        if (targets.includes('CATEGORY')){
            this._validoptions = null;     
            this._validcategories = null;
            this._validtocart = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCTOPT')){
            this._validoptions = null;
            this._validselects = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // update registrations when children change
        this._subscriptions.push(
            this.product.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) self.doRegister();
            })
        );

        // register to updates on place products
        if (this.product.place && this.product.isgroup){
            this._subscriptions.push(
                this.product.place.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })    
            )    
        }

        // register to updates on contained items          
        this._subscriptions.push(
            this.product.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        );

        for(const select of this.selects){
            this._subscriptions.push(
                select.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })
            );
        }
        
        for(const category of this.categories){
            this._subscriptions.push(
                category.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/

    get family(): FamilyView | null {
        return this.data.alives.get(this.product.family) as FamilyView | null;
    }

    get last(): ProductView | null {
        if (!this._lastproduct){
            const _candidate = this.product.last;
            if (!_candidate?.IsValid){
                this._lastproduct = null;
                return this._lastproduct;
            }

            this._lastproduct = this.data.alives.get(_candidate) as ProductView;
        }

        return this._lastproduct;
    }

    /**
     * Whether this product and `product` are the same logical place row
     * after following the catalog replacement chain (`last`).
     */
    equal(product: ProductView | null): boolean {
        if (!product){
            return false;
        }

        if (product.object === this.product){
            return true;
        }

        const _self = this.last ?? (this.data.alives.get(this.product) as ProductView);
        return _self === (product.last ?? product);
    }

    get parent(): ProductView | null {
        const _parent = this.product.parent;
        if (!_parent?.IsValid) {
            return null;
        }

        return this.data.alives.get(_parent) as ProductView | null;
    }

    set parent(value: ProductView | null){
        this.product.parent = value ? value.object : null;

        const place = this.data.alives.get(this.product.place) as PlaceView | null;
        if (place){
            place.DoRefresh("PRODUCT");
        }
    }

    get selects(): Array <PreselectView> {
        if (this._validselects == null){
            const _selects: Array <PreselectView> = []; 

            for(const _select of this.product.selects){
                const _selectview = this.alives.get(_select) as PreselectView;
                if (_selectview && _selectview.IsValid){
                    _selects.push(_selectview);
                }
            }

            _selects.sort((a, b) => {
                return (a.sort || 0) - (b.sort || 0);
            })

            this._validselects = _selects;
        }

        return this._validselects;
    }

    get categories(): Array <CategoryView> {
        if (this._validcategories === null){
            const _categories: Array <CategoryView> = [];

            for (const _category of this.product.categories){
                const _categoryview = this.alives.get(_category) as CategoryView;
                if (_categoryview && _categoryview.IsValid){
                    _categories.push(_categoryview);
                } 
            }

            _categories.sort((a, b) => {
                return (a.sort || 0) - (b.sort || 0);
            })

            this._validcategories = _categories;
        }

        return this._validcategories;
    }

    get options(): Array <ProductOptView> {
        if (this._validoptions === null){
            const _options: Array <ProductOptView> = [];

            for(const _category of this.categories){
                for (const _option of _category.options){
                    _options.push(_option);
                }
            }

            this._validoptions = _options;
        }

        return this._validoptions;
    }

    get products(): Array <ProductView> {
        if (this._groupitems === null){
            const _products: Array <ProductView> = [];

            this._productslength = 0;
            this._catalogslength = 0;

            if (!this.product.isgroup){
                return [];
            }

            const _place: PlaceView = this.data.alives.get(this.product.place) as PlaceView; 
            for (const _product of _place.products){
                if (_product.parent && (_product.parent.object == this.product)){
                    _products.push(_product);

                    if (_product.isgroup){
                        this._productslength += _product.IsFamily ? 0 : _product.ProductsLength;
                        this._catalogslength += _product.IsFamily ? 0 : _product.CatalogsLength;
                    }
                    else {  /* IsProduct or IsGeneric */
                        if (_product.IsCatalog){
                            this._catalogslength++;
                        }
                        else {
                            this._productslength++;
                        }
                    }
                }
            }

            this._groupitems = _products;
        }

        return this._groupitems;
    }

    // calculated at the same time as the products list
    get ProductsLength(): number {
        if (this.IsFamily){
            return this.family?.ProductsLength || 0;
        }

        if (this.products.length === 0){
            return 0;   // this forces the products calculation
        }

        return this._productslength;
    }

    // calculated at the same time as the products list
    get CatalogsLength(): number {
        if (this.IsFamily){
            return 0;   // no catalog products inside families
        }

        if (this.products.length === 0){
            return 0;   // this forces the products calculation
        }

        return this._catalogslength;
    }

    /**********************************/
    /* PRODUCT TYPE                   */
    /**********************************/

    get IsGroup(): boolean {
        return (this.product.isgroup && !this.product.isfamily) || false;
    }

    get IsFamily(): boolean {
        return (this.product.isgroup && this.product.isfamily) || false;
    }

    get IsGeneric(): boolean {
        return this.product.generic || false;
    }

    get IsProduct(): boolean {
        return (!this.product.isgroup && !this.product.generic) || false;
    }

    get IsVarious(): boolean {
        return this.product.status == 'OT';
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    get IsAvailable(): boolean {
        return (this.product.status == 'AC') || (this.product.status == 'OT');
    }

    /**
     * Determines if a product has configuration shortcuts or, in the case of a group/family, contains at least one non-catalog element.
     * 
     * @returns {boolean} 
     *   - For groups or families: false if the product contains at least one non-catalog element.
     *   - For regular products: false if the product provides at least one configuration shortcut.
     */
    get IsEmpty(): boolean {
        if (this.IsGroup || this.IsFamily){
            return !this.products.some(_product => {
                return (_product.IsCatalog == false);
            });
        }

        return (this.selects.length == 0);
    }

    private _checked = false;
    get Checked(): boolean {
        return this._checked;
    }

    set Checked(value: boolean){
        this._checked = value;
        this.product.DoRefresh('PRODUCTCHECKED');
    }

    get CanAvailable(): boolean {
        let _parent = this.parent || null;
        while (_parent != null){
            if (!_parent.IsAvailable){
                return false;
            }

            _parent = _parent.parent;
        }

        return true;        
    }
      
    /**********************************/
    /* PRODUCT PRICE                  */
    /**********************************/

    /**
     * Minimum charge for a typical configuration (product + cheapest SINGLE/FIXED options).
     * Pass `place`/`qrcode`/`at` to resolve each amount via zone/period (`tableprice`);
     * omit them (or pass `null`) for the base catalog price.
     */
    MinPrice(place: PlaceView | null = null, qrcode: QrCodeView | null = null, at: Date = new Date()): PriceType | null {
        if (this.product.IsMarket){
            return null;
        }

        let _price = this.tableprice(place, qrcode, at);

        for (const _category of this.categories){
            const options = _category.options.slice(0);

            options.sort((a, b) => {
                const _a: number = a?.tableprice(place, qrcode, at) || 0;
                const _b: number = b?.tableprice(place, qrcode, at) || 0;

                return _a - _b;
            });

            if (_category.type == 'SINGLE'){
                _price += options[0]?.tableprice(place, qrcode, at) || 0;
            }

            if (_category.type == 'FIXED'){
                for (let n = 0; n < (_category.fxmin || 0); n++){
                    _price += options[n]?.tableprice(place, qrcode, at) || 0;
                }
            }
        }

        const _p: number = _price || 0;
        const _r: number = this.product?.taxrate ?? AppConstants.defaultTaxRate;

        return new PriceInfo(_p, _r).price;
    }

    get priceinfo(): PriceType | null {
        if (!this.IsProduct || this.product.IsMarket){
            return null;    // no priceinfo is provided
        }

        if (this._calcpriceinfo == null){
            const _p: number = this.product.price || 0;
            const _r: number = this.product?.taxrate ?? AppConstants.defaultTaxRate;
    
            this._calcpriceinfo = new PriceInfo(_p, _r).price;
        }

        return this._calcpriceinfo;
    }

    /**
     * Resolves the price to charge for this product at the ticket's place/table, at instant `at`.
     * Effective zone is the table's zone, or the default zone (`null`) when the table has none / is bar.
     * Priority: active period override in the effective zone → custom zone's default override
     * (`pricetime == null`) → `product.price`. A period override never cascades into a different
     * zone than the one it belongs to.
     */
    tableprice(place: PlaceView | null, qrcode: QrCodeView | null, at: Date = new Date()): number {
        const _base = this.product.price || 0;

        if (!place){
            return _base;
        }

        const _zoneview = qrcode?.pricezone ?? null;
        const _zone = _zoneview?.object ?? null; // null == default zone

        const _period = place.ActiveTime(at);
        const _rows = this.product.prices ?? [];

        if (_period){
            const _periodrow = _rows.find((_row) => _row.IsValid && (_row.pricezone === _zone) && (_row.pricetime === _period));
            if (_periodrow){
                const _exact = _periodrow.price ?? null;
                if (_exact !== null){
                    return _exact;
                }

                // empty period row: fall through to zone default / base (same as GetPriceAt).
            }
        }

        // custom zone default override only (default zone never has pricetime == null)
        if (_zone){
            const _defaultrow = _rows.find((_row) => _row.IsValid && (_row.pricezone === _zone) && (_row.pricetime === null));
            if (_defaultrow){
                const _zoneDefault = _defaultrow.price ?? null;
                if (_zoneDefault !== null){
                    return _zoneDefault;
                }
            }
        }

        return _base;
    }

    /**********************************/
    /* PRODUCT SELECTION              */
    /**********************************/

    private get _IsAbleToCart(): boolean {
        if (this.product.IsMarket || this.product.IsWeighted || this.IsGeneric || this.IsVarious){
            return false;
        }

        for(const _category of this.categories){
            // at least one single category
            if (_category.type == 'SINGLE'){
                return false;   
            }

            // at least one fixed with forced selection
            if (_category.type == 'FIXED'){
                return (_category.fxmin === null) || (_category.fxmin > 0);
            }
        }

        return true;
    }

    get IsAbleToCart(): boolean {
        if (this._validtocart === null){
            this._validtocart = this._IsAbleToCart;
        }
        return this._validtocart;
    }

    MarkEnabled(){
        for (const _category of this.categories){
            _category.MarkEnabled();
        }
    }

    ResetChecked(){
        for (const _category of this.categories){
            _category.ResetChecked();
        }
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type ProductView = ViewType <_ProductView, Product>;

export function ProductViewProxy(object: Product, data: dataService): ProductView {
    return new _ProductView(object, data).Proxy as ProductView;
}
